import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  UnorderedList,
  ListItem,
  Link,
  Alert,
  AlertIcon,
  useColorModeValue,
  Divider
} from '@chakra-ui/react';
import SEO from '../SEO';
import { generateSEOMetadata, staticPages } from '../../utils/seo';

const TermsOfUse = () => {
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headingColor = useColorModeValue('gray.800', 'white');
  const textColor = useColorModeValue('gray.700', 'gray.300');

  // Generate SEO metadata for terms of use page
  const seoMetadata = generateSEOMetadata.staticPage(
    staticPages['terms-of-use'].name,
    staticPages['terms-of-use'].url,
    staticPages['terms-of-use'].description
  );

  return (
    <>
      <SEO {...seoMetadata} />
      <Box bg={useColorModeValue('gray.50', 'gray.900')} py={12}>
        <Container maxW="4xl" bg={bgColor} p={8} borderRadius="lg" boxShadow="md">
          <VStack spacing={8} align="stretch">
            <Box textAlign="center">
              <Heading as="h1" size="xl" mb={2} color="#4CAF50">
                Twygs Terms of Use
              </Heading>
              <Text color="gray.500" fontSize="sm">
                <strong>Effective Date:</strong> {currentDate}
              </Text>
              <Text color="gray.500" fontSize="sm">
                <strong>Last Updated:</strong> {currentDate}
              </Text>
            </Box>

            <Box
              p={5}
              bg={useColorModeValue('green.50', 'green.900')}
              borderLeft="4px solid"
              borderColor="#4CAF50"
              borderRadius="md"
            >
              <Text>
                Odele Technology, Inc., is a company registered in Delaware with the following
                details: 8 The Green, STE A, Dover, DE 19901 ("Odele", "we", "our"). Any use of this
                website is conditional on your acceptance of these terms of service and extends to
                our stated privacy policy.
              </Text>
            </Box>

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                1. Agreement to Terms
              </Heading>
              <Text color={textColor} mb={4}>
                We reserve the right to amend these Terms of Service (the "Terms") from time to time
                without notice and at our sole discretion. As such, it is your responsibility to
                review these stated Terms at regular intervals.
              </Text>
              <Text color={textColor}>
                We reserve the right to amend, modify, discontinue, or suspend all or part of this
                site and may restrict your access to all or part of 'the site' at any time without
                notice or liability.
              </Text>
              <Text color={textColor} mt={4}>
                Having read and for any reason do not agree with or cannot abide by these Terms,
                including our privacy policy, please exit this site immediately.
              </Text>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                2. Parties
              </Heading>
              <Text color={textColor} mb={4}>
                All Non-Fungible Tokens ("NFTs") are owned by the users themselves and Odele assists
                the users with marketing said NFTs for sale.
              </Text>
              <Text color={textColor} mb={4}>
                This ("Service") also allows you sell and purchase Crypto Assets. "Crypto Assets"
                shall be defined as, unique non-fungible tokens, implemented on the Ethereum
                blockchain (the "Ethereum Platform"), and all other platforms available now, or in
                the future, using smart contracts. ALL TRANSACTIONS INITIATED THROUGH OUR SERVICE
                ARE FACILITATED AND RUN BY THIRD-PARTY ELECTRONIC WALLET EXTENSIONS, AND BY USING
                OUR SERVICES YOU AGREE THAT YOU ARE GOVERNED BY THE TERMS OF SERVICE AND PRIVACY
                POLICY FOR THE APPLICABLE EXTENSIONS.
              </Text>
              <Text color={textColor} mb={4}>
                ODELE IS A PLATFORM, NOT A BROKER, FINANCIAL INSTITUTION, OR CREDITOR. THE SERVICES
                ARE INTENDED TO BE AN ADMINISTRATIVE PLATFORM ONLY. ODELE FACILITATES TRANSACTIONS
                BETWEEN THE BUYER AND SELLER IN THE AUCTION/SALE BUT IS NOT A PARTY TO ANY AGREEMENT
                BETWEEN THE BUYER AND SELLER OF CRYPTO ASSETS OR BETWEEN ANY USERS.
              </Text>
              <Alert
                status="warning"
                variant="left-accent"
                borderLeftColor="#4CAF50"
                bg={useColorModeValue('green.50', 'green.900')}
                mt={4}
              >
                <AlertIcon color="#4CAF50" />
                <Text fontWeight="medium">
                  THESE TERMS OF USE ARE IMPORTANT AND AFFECT YOUR LEGAL RIGHTS, SO PLEASE READ THEM
                  CAREFULLY. WE WANT TO LET YOU KNOW THAT THE TERMS INCLUDE AN ARBITRATION AGREEMENT
                  WHICH WILL, WITH LIMITED EXCEPTIONS, REQUIRE DISPUTES BETWEEN US TO BE SUBMITTED
                  TO BINDING AND FINAL ARBITRATION.
                </Text>
              </Alert>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                3. Title and Ownership
              </Heading>
              <Text color={textColor}>
                Title to, and ownership of, all NFTs passes from the supplier to the purchaser.
                Odele will not be liable for infringement of copyrights, for any material that is
                provided by the user and we reserve the right to ask the user for a valid proof of
                ownership or purchase of copyright on the user's end, to ensure no material is used
                without the actual owner's permission.
              </Text>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                4. Choice of Law; Arbitration; and Class Action Waiver
              </Heading>
              <Text color={textColor}>
                The internal laws of Delaware, without regard to any otherwise applicable choice of
                laws principles, shall govern any action related to these Terms of Service, the use
                of the Odele site or the Odele business.
              </Text>
              <Text color={textColor} mt={4} fontWeight="bold">
                YOU ACKNOWLEDGE AND AGREE THAT DISCOVERY AND APPEAL RIGHTS ARE LIMITED IN
                ARBITRATION.
              </Text>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                5. Class Action Waiver
              </Heading>
              <Text color={textColor} fontWeight="bold">
                ARBITRATION MUST BE ON AN INDIVIDUAL BASIS. THIS MEANS THAT NEITHER YOU NOR ODELE
                MAY JOIN OR CONSOLIDATE CLAIMS IN ARBITRATION BY OR AGAINST OTHER INDIVIDUALS OR
                LITIGATE IN COURT OR ARBITRATE ANY CLAIMS AS A REPRESENTATIVE OR MEMBER OF A CLASS
                OR IN A PRIVATE ATTORNEY GENERAL CAPACITY.
              </Text>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                6. Agreement to Arbitrate
              </Heading>
              <Text color={textColor}>
                In the event of a dispute between you and us arising under or relating to the Odele
                site or the Odele business, you hereby acknowledge and agree that by using the Odele
                site, you are irrevocably agreeing to resolve any such dispute or claim through
                binding arbitration, as described below, instead of bringing an action in court.
                THIS MEANS THAT NEITHER PARTY SHALL HAVE THE RIGHT TO LITIGATE SUCH DISPUTE OR CLAIM
                IN COURT OR TO HAVE A JURY TRIAL.
              </Text>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                7. Acceptance of and Changes to Terms of Service
              </Heading>
              <Text color={textColor}>
                By using our site and services, you signify your acceptance of the Agreement,
                including, without limitation, all the terms and conditions in these Terms of
                Service. If you do not agree completely with any terms, conditions, disclaimers,
                limitations or other provisions in your Agreement with us, your only remedy is to
                discontinue use of our site. We reserve the right to modify our Agreement with you,
                including, without limitation, these Terms of Service at any time. Your continued
                use of any portion of our site following the effective date contained in the
                notification or the posting of such changes on our site if no other effective date
                is specified, will constitute your acceptance of those changes and agreement to
                comply with all the then current terms and conditions of the Agreement.
              </Text>
              <Text color={textColor} mt={4}>
                If you have questions or concerns about our specific terms, please send an e-mail to{' '}
                <Link color="#4CAF50" href="mailto:hello@twygs.io">
                  hello@twygs.io
                </Link>
              </Text>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                8. User Obligations
              </Heading>
              <Text color={textColor} mb={4}>
                You agree not to do any of the following while using Odele site:
              </Text>
              <UnorderedList spacing={2} pl={4}>
                <ListItem>
                  Intentionally or knowingly violate any applicable law or regulation or agreement
                  to which you are bound – including this Agreement – nor the rights of any other
                  party;
                </ListItem>
                <ListItem>
                  Misrepresent or try to deceive us as to your identity or the identity of anyone
                  else, use buying agents or conduct fraudulent activities;
                </ListItem>
                <ListItem>
                  Exceed authorized access, tamper with, or misuse any areas of Odele's website or
                  Odele's website's computer systems, resources, programming, code or communications
                  capabilities, nor any features or functions of our sites. Anyone who does so or
                  attempts to do so may be subject to prosecution.
                </ListItem>
                <ListItem>
                  Frame or link to Odele's site or any of our other sites, unless permitted in
                  writing by Odele.
                </ListItem>
              </UnorderedList>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                9. Third Party Content and Monitoring
              </Heading>
              <Text color={textColor}>
                Any opinions, advice, statements, services, offers, events or other information or
                content expressed or made available on our Web site by any third parties (including
                information providers and users) are those of the respective author(s) or
                distributor(s) and not ours. We neither endorse nor are responsible for these, in
                any way, including, without limitation, the accuracy or reliability of any opinion,
                advice, information or statement made by anyone other than our employee who is
                authorized by us and is acting in her or his official capacity.
              </Text>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                10. Links to Other Sites
              </Heading>
              <Text color={textColor}>
                Our site may contain links to other websites and/or resources. These links are
                provided as a convenience to you. Odele does not control and is not responsible for
                the content of such third-party sites, websites, or applications or the conduct of
                the operators of such third-party sites, websites, or applications, and does not
                make any representations regarding the accuracy, copyright or other statutory or
                regulatory compliance, legality, or decency of any of the content or other materials
                on such third-party sites, websites, or applications. Odele encourages you to
                exercise discretion while browsing the Internet. If you decide to access linked
                third-party sites, websites, or applications, you do so at your own risk. Your use
                of all third-party sites, websites, or applications are subject to the applicable
                policies of those third parties.
              </Text>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                11. Copyright Notice
              </Heading>
              <Text color={textColor}>
                All text, graphics, logos, icons, images, audio clips, video clips and software on
                the site ("Content") are copyrighted materials owned by or licensed to us. Content
                may contain trademarks, service marks and trade names which are owned by us and may
                also contain brand and product names which are trademarks, service marks or trade
                names which are owned us or by third parties and the term "Content" will be used and
                mean to include these as well. Unless authorized in writing by an officer of Odele,
                you may not use any Content without our prior written consent and even if we
                consent, in any manner that is likely to cause confusion among customers, or in any
                manner that disparages or discredits us or the copyright owner. You may not sell,
                reproduce, distribute, copy, duplicate, resell, modify, display, publicly perform,
                prepare derivative works based on, frame, mirror, repost, exploit for any commercial
                purpose, or otherwise use any of the Content in any way for any public or commercial
                purpose without our prior written consent or the consent of the rights holder if not
                us.
              </Text>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                12. Disclaimer of Warranties
              </Heading>
              <Text color={textColor} mb={4}>
                Although we endeavor to provide current, accurate and reliable information on our
                site, we cannot and do not warrant, promise, guarantee or make any representations
                regarding the accuracy, security, reliability or any use of the functions, features,
                operations, Content or information. We cannot and do not warrant your use of our
                site, or the operation or function of the our site, any component, feature,
                function, capability or offer or any products, software or services, will be
                uninterrupted or error free, or that defects or malfunctions will be corrected or
                that the site is free of viruses or other harmful elements.
              </Text>
              <Text color={textColor} fontWeight="bold">
                YOUR USE OF OUR SITE IS SOLELY AND FULLY AT YOUR OWN RISK AND YOU ASSUME FULL
                RESPONSIBILITY FOR ALL COSTS AND EXPENSES ASSOCIATED WITH SERVICING AND/OR REPAIR IN
                ANY WAY CONNECTED OR ARISING FROM ATTEMPTED, ALLEGED OR ACTUAL USE OR ACCESS OF OUR
                SITE. OUR SITE AND THE CONTENT IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT ANY
                EXPRESS, IMPLIED OR STATUTORY WARRANTY OF ANY KIND INCLUDING WARRANTIES OF
                MERCHANTABILITY, TITLE, NON-INFRINGEMENT OF THIRD-PARTY RIGHTS, OR FITNESS FOR ANY
                PARTICULAR PURPOSE.
              </Text>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                13. Limitation of Liability
              </Heading>
              <Text color={textColor} mb={4}>
                In no event shall we and/or our officers, directors, partners, owners, agents,
                contractors, representatives and/or the jewelry brand and/or retailers who fulfill
                your orders, be liable to you or anyone else for any indirect, punitive, incidental,
                special or consequential damages or any damages whatsoever, including, without
                limitation, damages for loss of use, data or profits, arising out of or in any way
                connected with our Website, this Agreement, the use or performance of our Website,
                the delay or inability to use our Website, the provision of or failure to provide
                services, or for any events, information, software, products, services and related
                graphics obtained through the our Web site, or otherwise arising out of the use of
                our Website, whether based on contract, tort, negligence, strict liability or
                otherwise, even if we or any other party may have been advised of the possibility of
                damages.
              </Text>
              <Text color={textColor}>
                If you are dissatisfied with any portion of our website, or with any part of this
                Agreement or your transactions with us, your sole and exclusive remedy is to
                discontinue using our Website. This sole and exclusive remedy is separate and
                independent of any other provision that limits our liability or your remedies under
                this Agreement.
              </Text>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                14. Indemnity
              </Heading>
              <Text color={textColor}>
                You agree to defend, indemnify, and hold Odele, its affiliates, employees, officers,
                directors and agents ("Odele Parties") harmless from and against any and all claims,
                damages, losses, costs, investigations, liabilities, judgments, fines, penalties,
                settlements, interest and expenses (including attorneys' fees) that directly or
                indirectly arise from or are related to any claim, suit, action, demand, or
                proceeding made or brought against any Odele Party, or on account of the
                investigation, defense, or settlement thereof, arising out of or in connection with,
                whether occurring heretofore or hereafter: (i) any content you post or upload on the
                site; (ii) your use of the site and your activities in connection with the site;
                (iii) your breach or alleged breach of these Terms of Service or the representations
                or warranties that you have made in these Terms of Service; (iv) your violation or
                alleged violation of any laws, rules, regulations, codes, statutes, ordinances or
                orders of any governmental or regulatory authorities in connection with your use of
                the site or your activities in connection with the site; (v) information or material
                transmitted through your device used to access the site, even if not submitted by
                you, that infringes, violates or misappropriates any copyright, trademark, trade
                secret, trade dress, patent, publicity, privacy, or other right of any person or
                entity; (vi) any misrepresentation made by you; and (vii) Odele Parties' use of the
                information that you submit to us (all of the foregoing, "Claims and Losses").
              </Text>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                15. Privacy Policy
              </Heading>
              <Text color={textColor}>
                Your use of our website is also subject to our{' '}
                <Link color="#4CAF50" href="/privacy-policy">
                  Privacy Policy
                </Link>
                .
              </Text>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                16. Termination of Use
              </Heading>
              <Text color={textColor}>
                We may terminate your Odele account with or without cause at any time effective
                immediately. You are personally liable for any orders that you place or charges that
                you incur prior to termination. We reserve the right to change, suspend or
                discontinue all or any aspects of the site at any time without prior notice.
              </Text>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                17. Contact Us
              </Heading>
              <Text color={textColor} mb={2}>
                If you have any questions or concerns about these Terms of Service, please contact
                us at:
              </Text>
              <Text color={textColor}>
                Email:{' '}
                <Link color="#4CAF50" href="mailto:hello@twygs.io">
                  hello@twygs.io
                </Link>
              </Text>
            </Box>
          </VStack>
        </Container>
      </Box>
    </>
  );
};

export default TermsOfUse;
